export const differenceInDays = (dateA: Date, dateB: Date): number => {
  const diffTime = Math.abs(dateA.getTime() - dateB.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const isValidDate = (date: Date | string): boolean => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return (
    Object.prototype.toString.call(date) === '[object Date]' &&
    !Number.isNaN(date.getTime())
  );
};

/**
 *
 * @param {Date | string | null} date the date to format
 * @returns iso string containing only the date portion
 * @brief converts a date to an iso string containing only the date portion, if `date` is not a Date object, the current date is used
 */
export const toIsoDate = (date: Date | string | null = new Date()): string => {
  if (date === null) {
    return toIsoDate();
  }
  let dateObj: Date = typeof date === 'string' ? new Date(date) : date;
  const type = Object.prototype.toString.call(dateObj);
  if (type !== '[object Date]') {
    console.error(
      `toIsoDate: date must be a Date object or string, not a ${type}, defaulting to current date`,
    );
    return toIsoDate();
  }
  if (Number.isNaN(dateObj.getTime())) {
    console.error(
      `toIsoDate: date must be a valid Date object, defaulting to current date`,
    );
    return toIsoDate();
  }
  // Use UTC time components to avoid local time zone date rollovers
  const year = dateObj.getUTCFullYear();
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toDate = (date: string | Date): Date | null => {
  if (date instanceof Date) {
    return date;
  }
  if (!isValidDate(date)) {
    console.error(`toDate: invalid date string "${date}"`);
    return null;
  }
  return new Date(date);
};

export const toUTCDate = (date: Date | string): Date => {
  if(!date) {
    throw new Error('toUTCDate: date is required');
  }
  if (typeof date === 'string') {
    const _date = toDate(date);
    if (!_date) {
      throw new Error(`toUTCDate: invalid date string "${date}"`);
    }
    date = _date;
  }
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
};

export const toDateString = (date: Date | string) => {
  return (
    toDate(date)?.toLocaleDateString(undefined, {
      timeZone: 'UTC',
    }) ?? 'Invalid Date'
  );
};
